// frontend/src/pages/EditorialBoard.js – focused manuscript editorial workspace
// Build: 2026-07-09-editorial-board-page-v5-4-scoped-status-and-local-project-shell
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { useUser } from "../UserContext";
import { api } from "../api";
import ProjectBreadcrumbTrail from "../components/ProjectBreadcrumbTrail";

const BOARD_LIST_LIMIT = 200;

function safeText(value) {
  return String(value || "").trim();
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function getId(value) {
  return value?._id || value?.id || null;
}

function getIdAliases(value) {
  const out = [];
  const a = safeText(value?._id);
  const b = safeText(value?.id);
  if (a) out.push(a);
  if (b && b !== a) out.push(b);
  return out;
}

function normalizeStoriesListResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.stories)) return payload.stories;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function parseStoryIdFromUrl() {
  try {
    const qs = new URLSearchParams(window.location.search || "");
    return safeText(qs.get("storyId") || qs.get("id"));
  } catch {
    return "";
  }
}

function setEditorialBoardStoryIdInUrl(storyId) {
  try {
    const id = safeText(storyId);
    const base = "/editorial-board";
    window.history.replaceState({}, "", id ? `${base}?storyId=${encodeURIComponent(id)}` : base);
  } catch {}
}

function normalizeForSearch(value) {
  return safeText(value).toLowerCase();
}

function storyAuthorName(story, fallback = "Unknown") {
  return (
    safeText(story?.authorName) ||
    safeText(story?.writerName) ||
    safeText(story?.createdByName) ||
    safeText(story?.author?.name) ||
    safeText(story?.author?.username) ||
    fallback
  );
}

function normalizedToken(value) {
  return safeText(value).toLowerCase();
}

function currentUserIdentityGroups(user) {
  return {
    strong: new Set(
      [
        user?._id,
        user?.id,
        user?.userId,
        user?.uid,
        user?.sub,
        user?.email,
        user?.username,
        user?.userName,
      ]
        .map(normalizedToken)
        .filter(Boolean)
    ),
    legacy: new Set(
      [user?.name, user?.fullName, user?.displayName]
        .map(normalizedToken)
        .filter(Boolean)
    ),
  };
}

function storyOwnerIdentityGroups(story) {
  const authorProfile = story?.authorProfile && typeof story.authorProfile === "object" ? story.authorProfile : {};
  const authorObject = story?.author && typeof story.author === "object" ? story.author : {};
  const members = safeArray(story?.group?.members);
  const ownerMembers = members.filter((member) => {
    const role = normalizedToken(member?.role);
    return role === "owner" || role === "author" || role === "initiator";
  });

  const strong = [
    story?.authorId,
    story?.ownerId,
    story?.userId,
    story?.writerId,
    story?.createdById,
    story?.createdByUserId,
    story?.authorUsername,
    story?.ownerUsername,
    story?.writerUsername,
    story?.createdByUsername,
    story?.username,
    story?.authorEmail,
    story?.ownerEmail,
    story?.writerEmail,
    story?.createdByEmail,
    authorObject?._id,
    authorObject?.id,
    authorObject?.userId,
    authorObject?.username,
    authorObject?.email,
    authorProfile?._id,
    authorProfile?.id,
    authorProfile?.userId,
    authorProfile?.username,
    authorProfile?.email,
  ];

  const legacy = [
    story?.authorName,
    story?.ownerName,
    story?.writerName,
    story?.createdBy,
    story?.createdByName,
    typeof story?.author === "string" ? story.author : "",
    authorObject?.name,
    authorProfile?.name,
  ];

  ownerMembers.forEach((member) => {
    strong.push(member?._id, member?.id, member?.userId, member?.username, member?.email);
    legacy.push(member?.name);
  });

  return {
    strong: strong.map(normalizedToken).filter(Boolean),
    legacy: legacy.map(normalizedToken).filter(Boolean),
  };
}
function isStoryOwnedByCurrentUser(story, user) {
  const userGroups = currentUserIdentityGroups(user);
  const groups = storyOwnerIdentityGroups(story);
  if (groups.strong.length) {
    return groups.strong.some((token) => userGroups.strong.has(token));
  }
  return groups.legacy.some((token) => userGroups.strong.has(token) || userGroups.legacy.has(token));
}

function storyMatchesQuery(story, query) {
  const q = normalizeForSearch(query);
  if (!q) return true;
  const hay = [
    story?.title,
    story?.forum,
    story?.writingMode,
    story?.description,
    story?.synopsis,
    storyAuthorName(story, ""),
  ].map(normalizeForSearch).join(" ");
  return hay.includes(q);
}

function safeSceneText(scene) {
  return safeText(scene?.text || scene?.body || scene?.content || scene?.sceneText || "");
}

function safeSceneTitle(scene, ui) {
  return safeText(scene?.title || scene?.sceneTitle || "") || ui.untitledChapter;
}

function approvedCanonScenes(story) {
  const scenes = safeArray(story?.scenes);
  return scenes
    .filter((scene) => safeText(scene?.type).toLowerCase() === "canon" && safeText(scene?.status).toLowerCase() === "approved")
    .slice()
    .sort((a, b) => new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime());
}

function countWords(value) {
  const text = safeText(value).replace(/\s+/g, " ");
  if (!text) return 0;
  return text.split(" ").filter(Boolean).length;
}

function clampWords(text, maxWords) {
  const t = safeText(text);
  if (!t) return "";
  const parts = t.split(/\s+/).filter(Boolean);
  if (parts.length <= maxWords) return t;
  return parts.slice(0, maxWords).join(" ") + "…";
}

function formatDateTime(iso, lang = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(String(lang).toLowerCase() === "fr" ? "fr" : "en", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function buildSoulMapStorageKey(storyId, canonCount, stamp) {
  const id = safeText(storyId);
  if (!id) return "";
  return `nextscenes:manuscript-editor:soul-map:${id}:${Number(canonCount || 0)}:${safeText(stamp) || "no-date"}`;
}

function buildDraftStorageKey(storyId) {
  const id = safeText(storyId);
  return id ? `nextscenes:manuscript-editor:editorial-drafts:${id}` : "";
}

const MANUSCRIPT_EDIT_MARKER_RE = /(\[\[\s*(?:NS_)?EDIT(?:ED)?\s*\]\]|\[\[\s*\/\s*(?:NS_)?EDIT(?:ED)?\s*\]\])/gi;
const MANUSCRIPT_EDIT_START_RE = /^\[\[\s*(?:NS_)?EDIT(?:ED)?\s*\]\]$/i;
const MANUSCRIPT_EDIT_END_RE = /^\[\[\s*\/\s*(?:NS_)?EDIT(?:ED)?\s*\]\]$/i;

function stripManuscriptEditMarkers(value) {
  return String(value || "")
    .replace(MANUSCRIPT_EDIT_MARKER_RE, "")
    .trim();
}

function manuscriptMarkedRevisionToPlainText(value) {
  return stripManuscriptEditMarkers(value);
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function manuscriptMarkedRevisionToHtml(value) {
  const source = String(value || "");
  if (!source) return "";
  let edited = false;
  const parts = source.split(MANUSCRIPT_EDIT_MARKER_RE);
  return parts.map((part) => {
    if (MANUSCRIPT_EDIT_START_RE.test(part)) {
      edited = true;
      return "";
    }
    if (MANUSCRIPT_EDIT_END_RE.test(part)) {
      edited = false;
      return "";
    }
    const escaped = escapeHtml(part).replace(/\n/g, "<br />");
    return edited ? `<span style="color:#b91c1c;font-style:italic;background:rgba(254,226,226,0.62);border-radius:5px;padding:0 3px;">${escaped}</span>` : escaped;
  }).join("");
}

function renderMarkedText(value) {
  const source = String(value || "");
  if (!source) return "";
  let edited = false;
  const parts = source.split(MANUSCRIPT_EDIT_MARKER_RE);
  return parts.map((part, idx) => {
    if (MANUSCRIPT_EDIT_START_RE.test(part)) {
      edited = true;
      return null;
    }
    if (MANUSCRIPT_EDIT_END_RE.test(part)) {
      edited = false;
      return null;
    }
    if (!part) return null;
    return edited ? <span className="ns-manuscript-edited-segment" key={`edit-${idx}`}>{part}</span> : part;
  });
}

async function copyTextToClipboard({ plainText, htmlText }) {
  const text = String(plainText || "");
  if (!text) throw new Error("Nothing to copy.");
  if (htmlText && typeof window !== "undefined" && navigator?.clipboard?.write && window.ClipboardItem) {
    const item = new window.ClipboardItem({
      "text/plain": new Blob([text], { type: "text/plain" }),
      "text/html": new Blob([String(htmlText)], { type: "text/html" }),
    });
    await navigator.clipboard.write([item]);
    return;
  }
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  throw new Error("Clipboard unavailable.");
}

function isRateLimitError(err) {
  const message = safeText(err?.message || err?.statusText || err);
  const status = Number(err?.status || err?.statusCode || err?.response?.status || 0);
  return status === 429 || /429|too many requests|rate limit/i.test(message);
}

function isAccessOrNotFoundError(err) {
  const message = safeText(err?.message || err?.statusText || err);
  const status = Number(err?.status || err?.statusCode || err?.response?.status || 0);
  return status === 403 || status === 404 || /access denied|forbidden|owner|not found/i.test(message);
}

function userFacingApiError(err, fallback, ui) {
  if (isRateLimitError(err)) return ui.rateLimited;
  return safeText(err?.message) || fallback;
}

function buildCompiledEditedManuscript({ story, canonScenes, drafts, ui, lang }) {
  const latestDraftByChapter = new Map();
  safeArray(drafts).forEach((draft) => {
    const idx = Number(draft?.chapterIndex);
    if (!Number.isFinite(idx) || idx < 0) return;
    if (!latestDraftByChapter.has(idx)) latestDraftByChapter.set(idx, draft);
  });

  const lines = [];
  const title = safeText(story?.title) || ui.untitled;
  const author = storyAuthorName(story, ui.unknown);
  lines.push(title);
  lines.push(`${ui.author}: ${author}`);
  lines.push("");
  lines.push(ui.compiledNotice);
  lines.push("");

  let usedDrafts = 0;
  safeArray(canonScenes).forEach((scene, idx) => {
    const draft = latestDraftByChapter.get(idx);
    const chapterTitle = safeText(draft?.chapterTitle) || safeSceneTitle(scene, ui);
    const body = draft?.cleanRevision ? stripManuscriptEditMarkers(draft.cleanRevision) : safeSceneText(scene);
    if (draft?.cleanRevision) usedDrafts += 1;
    lines.push(`${idx + 1}. ${chapterTitle}`);
    lines.push("");
    lines.push(body);
    lines.push("");
  });

  return {
    text: lines.join("\n").trim(),
    usedDrafts,
    canonFallbacks: Math.max(0, safeArray(canonScenes).length - usedDrafts),
    createdAt: new Date().toISOString(),
    language: lang,
  };
}

function buildLocalEditorialProject({ story, canonScenes, ui, lang }) {
  const chapters = safeArray(canonScenes);
  const title = safeText(story?.title) || ui.untitled;
  const chapterMap = chapters.map((scene, idx) => {
    const sceneTitle = safeSceneTitle(scene, ui);
    const sceneText = safeSceneText(scene);
    return {
      number: idx + 1,
      title: sceneTitle,
      summary: clampWords(sceneText, 22),
      function: clampWords(sceneText, 18),
    };
  });

  return {
    ok: true,
    build: "browser-local-editorial-shell-v1",
    mode: "local-editorial-project-shell",
    provider: "browser-local",
    soulMap: {
      editorialSummary: ui.localProjectSummary(title, chapters.length),
      centralTheme: ui.localProjectCentralTheme,
      voiceRules: [
        ui.localVoicePreserve,
        ui.localVoiceAvoidOverwriting,
        ui.localVoiceCheckContinuity,
      ],
      protectedElements: [
        ui.localProtectCanon,
        ui.localProtectCharacters,
        ui.localProtectPromises,
      ],
    },
    continuityLedger: {
      unresolvedPromises: [
        ui.localContinuityNames,
        ui.localContinuityTimeline,
        ui.localContinuityPromises,
      ],
    },
    chapterMap,
    nextSteps: [
      ui.localNextStepReviewMap,
      ui.localNextStepRunPass,
      ui.localNextStepRefresh,
    ],
    notice: ui.localProjectFallback,
    advisoryOnly: true,
    mutatesStory: false,
    localFallback: true,
    language: lang,
  };
}



function slugifyFileName(value, fallback = "manuscript") {
  const base = safeText(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70);
  return base || fallback;
}

function normalizeUploadedChapterTitle(raw, index, ui) {
  return safeText(raw) || `${ui.chapter || "Chapter"} ${index + 1}`;
}

function parsePlainTextManuscript(text, title, ui) {
  const source = safeText(text).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const fallbackTitle = safeText(title) || ui.uploadedManuscriptTitle;
  if (!source) return { title: fallbackTitle, chapters: [], manuscriptText: "" };

  const lines = source.split("\n");
  const chapterHeadingRe = /^\s*((chapter|scene|part|section|book)\s+([0-9ivxlcdm]+|one|two|three|four|five|six|seven|eight|nine|ten)\b.*|\d+\.\s+.+)$/i;
  const chapters = [];
  let current = null;

  lines.forEach((line) => {
    if (chapterHeadingRe.test(line) && safeText(line).length <= 140) {
      if (current && safeText(current.text)) chapters.push(current);
      current = { title: safeText(line).replace(/^\d+\.\s*/, ""), text: "" };
      return;
    }
    if (!current) current = { title: `${ui.chapter || "Chapter"} 1`, text: "" };
    current.text += (current.text ? "\n" : "") + line;
  });
  if (current && safeText(current.text)) chapters.push(current);

  const finalChapters = (chapters.length ? chapters : [{ title: `${ui.chapter || "Chapter"} 1`, text: source }])
    .map((chapter, idx) => ({
      number: idx + 1,
      title: normalizeUploadedChapterTitle(chapter.title, idx, ui),
      text: safeText(chapter.text).slice(0, 50000),
    }))
    .filter((chapter) => chapter.text);

  return {
    title: fallbackTitle,
    chapters: finalChapters,
    manuscriptText: finalChapters.map((chapter) => `${chapter.number}. ${chapter.title}\n\n${chapter.text}`).join("\n\n---\n\n"),
  };
}

function uploadedManuscriptToPseudoStory(uploaded, ui) {
  const chapters = safeArray(uploaded?.chapters);
  const id = safeText(uploaded?.uploadId) || `uploaded-${Date.now()}`;
  return {
    _id: id,
    id,
    title: safeText(uploaded?.title) || ui.uploadedManuscriptTitle,
    authorName: safeText(uploaded?.authorName) || ui.uploadedAuthor,
    forum: ui.uploadedSource,
    updatedAt: uploaded?.createdAt || new Date().toISOString(),
    createdAt: uploaded?.createdAt || new Date().toISOString(),
    scenes: chapters.map((chapter, idx) => ({
      _id: `${id}-chapter-${idx + 1}`,
      id: `${id}-chapter-${idx + 1}`,
      type: "canon",
      status: "approved",
      title: safeText(chapter?.title) || `${ui.chapter || "Chapter"} ${idx + 1}`,
      text: safeText(chapter?.text),
      createdAt: uploaded?.createdAt || new Date().toISOString(),
    })),
  };
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function escapeXml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function crc32Bytes(bytes) {
  let crc = 0 ^ -1;
  for (let i = 0; i < bytes.length; i += 1) {
    crc = (crc >>> 8) ^ CRC32_TABLE[(crc ^ bytes[i]) & 0xff];
  }
  return (crc ^ -1) >>> 0;
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function u16(value) {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value) {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
}

function makeZipBlob(files, mimeType) {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let offset = 0;
  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const dataBytes = typeof file.content === "string" ? encoder.encode(file.content) : new Uint8Array(file.content || []);
    const crc = crc32Bytes(dataBytes);
    const localHeader = new Uint8Array([
      ...u32(0x04034b50), ...u16(20), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
      ...u32(crc), ...u32(dataBytes.length), ...u32(dataBytes.length), ...u16(nameBytes.length), ...u16(0),
    ]);
    localParts.push(localHeader, nameBytes, dataBytes);

    const centralHeader = new Uint8Array([
      ...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
      ...u32(crc), ...u32(dataBytes.length), ...u32(dataBytes.length), ...u16(nameBytes.length), ...u16(0), ...u16(0),
      ...u16(0), ...u16(0), ...u32(0), ...u32(offset),
    ]);
    centralParts.push(centralHeader, nameBytes);
    offset += localHeader.length + nameBytes.length + dataBytes.length;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const centralOffset = offset;
  const end = new Uint8Array([
    ...u32(0x06054b50), ...u16(0), ...u16(0), ...u16(files.length), ...u16(files.length),
    ...u32(centralSize), ...u32(centralOffset), ...u16(0),
  ]);
  return new Blob([...localParts, ...centralParts, end], { type: mimeType });
}

function createDocxBlobFromText(title, text) {
  const now = new Date().toISOString();
  const paragraphs = String(text || "")
    .split(/\n+/)
    .map((line) => safeText(line))
    .filter(Boolean)
    .map((line, idx) => {
      const isTitle = idx === 0 || /^\d+\.\s+/.test(line);
      return `<w:p><w:pPr>${isTitle ? '<w:pStyle w:val="Heading1"/>' : ""}</w:pPr><w:r><w:t xml:space="preserve">${escapeXml(line)}</w:t></w:r></w:p>`;
    })
    .join("\n");
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${paragraphs}<w:sectPr><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/></w:sectPr></w:body></w:document>`;
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/><Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/></Types>`;
  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/><Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/></Relationships>`;
  const docRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"/>`;
  const core = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"><dc:title>${escapeXml(title)}</dc:title><dc:creator>NextScenes Editorial Board</dc:creator><cp:lastModifiedBy>NextScenes Editorial Board</cp:lastModifiedBy><dcterms:created xsi:type="dcterms:W3CDTF">${now}</dcterms:created><dcterms:modified xsi:type="dcterms:W3CDTF">${now}</dcterms:modified></cp:coreProperties>`;
  const app = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes"><Application>NextScenes</Application></Properties>`;
  return makeZipBlob([
    { name: "[Content_Types].xml", content: contentTypes },
    { name: "_rels/.rels", content: rels },
    { name: "word/document.xml", content: documentXml },
    { name: "word/_rels/document.xml.rels", content: docRels },
    { name: "docProps/core.xml", content: core },
    { name: "docProps/app.xml", content: app },
  ], "application/vnd.openxmlformats-officedocument.wordprocessingml.document");
}

function pdfHexString(value) {
  const text = String(value || "");
  let hex = "FEFF";
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    hex += code.toString(16).padStart(4, "0").toUpperCase();
  }
  return `<${hex}>`;
}

function wrapTextForPdf(text, maxChars = 88) {
  const out = [];
  String(text || "").split(/\n/).forEach((paragraph) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) {
      out.push("");
      return;
    }
    let line = "";
    words.forEach((word) => {
      const next = line ? `${line} ${word}` : word;
      if (next.length > maxChars && line) {
        out.push(line);
        line = word;
      } else {
        line = next;
      }
    });
    if (line) out.push(line);
    out.push("");
  });
  return out;
}

function createPdfBlobFromText(title, text) {
  const lines = wrapTextForPdf(text, 88);
  const pages = [];
  const pageLines = [];
  lines.forEach((line) => {
    pageLines.push(line);
    if (pageLines.length >= 46) {
      pages.push(pageLines.splice(0));
    }
  });
  if (pageLines.length) pages.push(pageLines);
  if (!pages.length) pages.push([safeText(title) || "Manuscript"]);

  const objects = [];
  objects.push("<< /Type /Catalog /Pages 2 0 R >>");
  const kids = pages.map((_, idx) => `${3 + idx * 2} 0 R`).join(" ");
  objects.push(`<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>`);
  pages.forEach((page, idx) => {
    const pageObj = 3 + idx * 2;
    const contentObj = pageObj + 1;
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> >> >> /Contents ${contentObj} 0 R >>`);
    const body = ["BT", "/F1 11 Tf", "16 TL"];
    let y = 746;
    page.forEach((line) => {
      body.push(`1 0 0 1 72 ${y} Tm ${pdfHexString(line)} Tj`);
      y -= 16;
    });
    body.push("ET");
    const stream = body.join("\n");
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((obj, idx) => {
    offsets.push(pdf.length);
    pdf += `${idx + 1} 0 obj\n${obj}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
}

function uiForLang(lang) {
  const isFR = String(lang || "en").toLowerCase() === "fr";
  return {
    isFR,
    title: isFR ? "Comité éditorial" : "Editorial Board",
    kicker: isFR ? "ATELIER DU MANUSCRIT" : "MANUSCRIPT WORKSHOP",
    subtitle: isFR
      ? "Un espace clair pour préparer une carte éditoriale, relire les chapitres et sauvegarder des brouillons sans toucher au Canon."
      : "A clear space for building an editorial map, reviewing chapters, and saving drafts without touching Canon.",
    sourceTitle: isFR ? "Source du projet" : "Project Source",
    existingStory: isFR ? "Depuis une histoire Canon" : "From an existing Canon story",
    futureUpload: isFR ? "Téléverser un manuscrit" : "Upload manuscript",
    futureUploadBody: isFR
      ? "Importez un manuscrit DOCX, TXT ou Markdown, ou collez un texte. Le travail reste séparé du Canon."
      : "Import a DOCX, TXT, or Markdown manuscript, or paste text. The work stays separate from Canon.",
    uploadFile: isFR ? "Choisir un fichier" : "Choose file",
    uploadPasteLabel: isFR ? "Ou coller le manuscrit" : "Or paste manuscript text",
    uploadPastePlaceholder: isFR ? "Collez ici le texte du manuscrit…" : "Paste manuscript text here…",
    usePastedManuscript: isFR ? "Utiliser le texte collé" : "Use pasted manuscript",
    uploadingManuscript: isFR ? "Lecture du manuscrit…" : "Reading manuscript…",
    uploadReady: isFR ? "Manuscrit chargé. Créez le projet éditorial quand vous êtes prêt." : "Manuscript loaded. Create the editorial project when ready.",
    uploadFailed: isFR ? "Le manuscrit n’a pas pu être chargé. Essayez DOCX, TXT, Markdown ou collez le texte." : "The manuscript could not be loaded. Try DOCX, TXT, Markdown, or paste the text.",
    uploadedSource: isFR ? "Manuscrit téléversé" : "Uploaded manuscript",
    uploadedAuthor: isFR ? "Auteur du manuscrit" : "Manuscript author",
    uploadedManuscriptTitle: isFR ? "Manuscrit importé" : "Uploaded Manuscript",
    uploadedSourceNote: isFR ? "Source externe. Rien n’est ajouté au Canon." : "External source. Nothing is added to Canon.",
    sourceChapters: isFR ? "chapitres source" : "source chapters",
    chapter: isFR ? "Chapitre" : "Chapter",
    chooseStory: isFR ? "Choisir une histoire" : "Choose story",
    search: isFR ? "Rechercher titre, auteur, forum…" : "Search title, author, forum…",
    refreshStories: isFR ? "Rafraîchir les histoires" : "Refresh stories",
    openDashboard: isFR ? "Ouvrir Story Dashboard" : "Open Story Dashboard",
    openWriterStudio: isFR ? "Ouvrir Writer Studio" : "Open Writer Studio",
    createProject: isFR ? "Créer le projet éditorial" : "Create Editorial Project",
    refreshProject: isFR ? "Rafraîchir le projet éditorial" : "Refresh Editorial Project",
    creatingProject: isFR ? "Création du projet…" : "Creating project…",
    noStory: isFR ? "Choisissez une histoire pour commencer." : "Choose a story to begin.",
    ownerOnlyNote: isFR ? "Seules vos propres storylines sont listées ici." : "Only your own storylines are listed here.",
    noOwnedStories: isFR ? "Aucune storyline dont vous êtes l’auteur n’a été trouvée." : "No storylines owned by you were found.",
    notOwnerError: isFR ? "Accès refusé : seul l’auteur propriétaire peut ouvrir cette histoire dans le comité éditorial." : "Access denied: only the story owner can open this story in the Editorial Board.",
    noCanon: isFR ? "Cette histoire n’a pas encore de chapitres Canon approuvés." : "This story has no approved Canon chapters yet.",
    canonChapters: isFR ? "chapitres Canon" : "Canon chapters",
    words: isFR ? "mots" : "words",
    author: isFR ? "Auteur" : "Author",
    forum: isFR ? "Forum" : "Forum",
    updated: isFR ? "Mis à jour" : "Updated",
    advisoryOnly: isFR ? "Conseil IA seulement" : "AI advisory only",
    authorDecides: isFR ? "Lecture consultative seulement. L’auteur décide." : "Advisory editorial reading only. The author decides.",
    soulMap: isFR ? "Carte de l’âme du livre" : "Book Soul Map",
    editorialSummary: isFR ? "Résumé éditorial" : "Editorial Summary",
    voice: isFR ? "Voix" : "Voice",
    protect: isFR ? "À protéger" : "Protect These",
    continuity: isFR ? "Registre de continuité" : "Continuity Ledger",
    nextSteps: isFR ? "Prochaines étapes" : "Next Steps",
    chapterMap: isFR ? "Carte des chapitres" : "Chapter Map",
    chapterPass: isFR ? "Passe éditoriale du chapitre" : "Chapter Editorial Pass",
    chooseChapter: isFR ? "Choisir un chapitre" : "Choose chapter",
    editMode: isFR ? "Mode d’édition" : "Editing mode",
    lightPolish: isFR ? "Polissage léger" : "Light Polish",
    authorVoice: isFR ? "Voix de l’auteur" : "Author Voice Pass",
    continuityPass: isFR ? "Continuité" : "Continuity Pass",
    deepEditorial: isFR ? "Édition approfondie" : "Deep Editorial Pass",
    runPass: isFR ? "Lancer la passe éditoriale" : "Run Editorial Pass",
    runningPass: isFR ? "Édition du chapitre…" : "Editing chapter…",
    creatingProjectStatus: isFR ? "Création du projet éditorial en cours. Merci de patienter…" : "Creating the editorial project. Please wait…",
    runningPassStatus: isFR ? "Passe éditoriale en cours. Merci de patienter…" : "Editorial pass is running. Please wait…",
    stillWorking: isFR ? "Travail en cours. Patientez jusqu’à la fin avant de recliquer." : "Work in progress. Please wait until it finishes before clicking again.",
    rateLimited: isFR ? "Trop de requêtes. Patientez une minute, puis réessayez. Aucun Canon n’a été modifié." : "Too many requests. Please wait one minute, then try again. Canon was not changed.",
    result: isFR ? "Résultat éditorial" : "Editorial Result",
    decisionControls: isFR ? "Contrôles de décision de l’auteur" : "Author Decision Controls",
    decisionBody: isFR ? "Copiez ou sauvegardez cette proposition séparément. Rien ici ne modifie le Canon." : "Copy or save this proposal separately. Nothing here changes Canon.",
    copyClean: isFR ? "Copier la version propre" : "Copy Clean Revision",
    copyMarked: isFR ? "Copier la version marquée" : "Copy Marked Revision",
    saveDraft: isFR ? "Sauvegarder comme brouillon éditorial" : "Save as Editorial Draft",
    applyPostponed: isFR ? "Appliquer au Canon : reporté pour sécurité" : "Apply to Canon: postponed for safety",
    notes: isFR ? "Notes éditoriales" : "Editorial Notes",
    warnings: isFR ? "Alertes de continuité" : "Continuity Warnings",
    changes: isFR ? "Résumé des changements" : "Change Summary",
    marked: isFR ? "Version marquée avec changements" : "Marked Revision with Changes",
    clean: isFR ? "Version propre proposée" : "Clean Proposed Revision",
    savedDrafts: isFR ? "Brouillons éditoriaux sauvegardés" : "Saved Editorial Drafts",
    savedDraftsHelp: isFR ? "Ces brouillons restent séparés du Canon et sont gardés dans ce navigateur." : "These drafts stay separate from Canon and are kept in this browser.",
    copyCleanSmall: isFR ? "Copier propre" : "Copy clean",
    copyMarkedSmall: isFR ? "Copier marqué" : "Copy marked",
    projectSuccess: isFR ? "Projet éditorial créé. Le Canon reste inchangé." : "Editorial project created. Canon remains unchanged.",
    projectFailed: isFR ? "La création du projet éditorial a échoué." : "Editorial project creation failed.",
    localProjectFallback: isFR
      ? "La carte IA n’a pas pu être créée maintenant. Un plan éditorial local a été préparé depuis le Canon. Vous pourrez rafraîchir la carte IA plus tard. Le Canon reste inchangé."
      : "The AI map could not be completed right now. A local editorial shell was prepared from Canon. You may refresh the AI map later. Canon remains unchanged.",
    localProjectSummary: (title, count) => isFR
      ? `${title} contient ${count} chapitres Canon approuvés. Ce plan local sert de point de départ prudent jusqu’à ce que la carte IA soit rafraîchie.`
      : `${title} has ${count} approved Canon chapters. This local shell gives a safe starting point until the AI map is refreshed.`,
    localProjectCentralTheme: isFR ? "À confirmer par l’auteur pendant la lecture éditoriale." : "To be confirmed by the author during editorial reading.",
    localVoicePreserve: isFR ? "Préserver la voix déjà présente dans les chapitres Canon." : "Preserve the voice already present in the Canon chapters.",
    localVoiceAvoidOverwriting: isFR ? "Ne pas moderniser ou remplacer le style de l’auteur sans décision explicite." : "Do not modernize or replace the author’s style without an explicit decision.",
    localVoiceCheckContinuity: isFR ? "Vérifier le rythme, les répétitions utiles et les transitions avant toute correction lourde." : "Check rhythm, useful repetition, and transitions before any heavy correction.",
    localProtectCanon: isFR ? "Ne jamais modifier le Canon depuis le comité éditorial." : "Never modify Canon from the Editorial Board.",
    localProtectCharacters: isFR ? "Protéger les noms, les liens familiaux, les lieux et les rôles déjà établis." : "Protect already established names, family ties, places, and roles.",
    localProtectPromises: isFR ? "Surveiller les promesses narratives et les fils ouverts." : "Track narrative promises and open threads.",
    localContinuityNames: isFR ? "Relire les noms, titres, lieux et relations récurrents." : "Review recurring names, titles, places, and relationships.",
    localContinuityTimeline: isFR ? "Vérifier l’ordre des événements d’un chapitre à l’autre." : "Check the order of events from chapter to chapter.",
    localContinuityPromises: isFR ? "Noter les promesses ou mystères qui doivent payer plus tard." : "Note promises or mysteries that must pay off later.",
    localNextStepReviewMap: isFR ? "Ouvrir la carte des chapitres et vérifier le découpage." : "Open the chapter map and verify the structure.",
    localNextStepRunPass: isFR ? "Lancer une passe éditoriale sur un chapitre à la fois." : "Run one chapter editorial pass at a time.",
    localNextStepRefresh: isFR ? "Rafraîchir la carte IA quand le service est disponible." : "Refresh the AI map when the service is available.",
    passSuccess: isFR ? "Passe éditoriale terminée. Vérifiez avant de décider." : "Editorial pass complete. Review before deciding.",
    passFailed: isFR ? "La passe éditoriale a échoué." : "Editorial pass failed.",
    copyCleanSuccess: isFR ? "Version propre copiée. Le Canon reste inchangé." : "Clean revision copied. Canon remains unchanged.",
    copyMarkedSuccess: isFR ? "Version marquée copiée. Le Canon reste inchangé." : "Marked revision copied. Canon remains unchanged.",
    draftSaved: isFR ? "Brouillon sauvegardé séparément. Le Canon reste inchangé." : "Draft saved separately. Canon remains unchanged.",
    copyFailed: isFR ? "La copie a échoué. Sélectionnez le texte manuellement." : "Copy failed. Please select the text manually.",
    loginRequired: isFR ? "Connectez-vous comme auteur pour ouvrir le comité éditorial." : "Sign in as a writer to open the Editorial Board.",
    unknown: isFR ? "Inconnu" : "Unknown",
    untitled: isFR ? "Histoire sans titre" : "Untitled story",
    untitledChapter: isFR ? "Chapitre sans titre" : "Untitled chapter",
    notReady: isFR ? "Créez d’abord le projet éditorial." : "Create the editorial project first.",
    localOnly: isFR ? "Sauvegarde locale séparée" : "Separate local save",
    recompile: isFR ? "Recompiler le manuscrit édité" : "Recompile Edited Manuscript",
    recompileHelp: isFR
      ? "Combine les derniers brouillons sauvegardés avec les chapitres Canon restants. Rien n’est appliqué au Canon."
      : "Combines the latest saved drafts with the remaining Canon chapters. Nothing is applied to Canon.",
    compiledTitle: isFR ? "Manuscrit édité compilé" : "Compiled Edited Manuscript",
    compiledNotice: isFR
      ? "Compilation consultative depuis les brouillons éditoriaux sauvegardés. Le Canon reste inchangé."
      : "Advisory compilation from saved editorial drafts. Canon remains unchanged.",
    usedDrafts: isFR ? "chapitres édités utilisés" : "edited chapters used",
    canonFallbacks: isFR ? "chapitres Canon inchangés" : "Canon chapters unchanged",
    copyCompiled: isFR ? "Copier le manuscrit compilé" : "Copy Compiled Manuscript",
    exportDocx: isFR ? "Exporter DOCX" : "Export DOCX",
    exportPdf: isFR ? "Exporter PDF" : "Export PDF",
    exportDocxSuccess: isFR ? "DOCX exporté. Le Canon reste inchangé." : "DOCX exported. Canon remains unchanged.",
    exportPdfSuccess: isFR ? "PDF exporté. Le Canon reste inchangé." : "PDF exported. Canon remains unchanged.",
    exportFailed: isFR ? "L’export a échoué. Copiez le manuscrit compilé et réessayez." : "Export failed. Copy the compiled manuscript and try again.",
    compiledReady: isFR ? "Manuscrit édité compilé. Le Canon reste inchangé." : "Edited manuscript compiled. Canon remains unchanged.",
    copyCompiledSuccess: isFR ? "Manuscrit compilé copié. Le Canon reste inchangé." : "Compiled manuscript copied. Canon remains unchanged.",
  };
}

export default function EditorialBoard({
  onNavigatePage,
  projectHubContext = {},
}) {
  const { isAuthenticated, user } = useUser();
  const lang = (typeof window !== "undefined" && window.localStorage.getItem("ns.lang")) || "en";
  const ui = useMemo(() => uiForLang(lang), [lang]);

  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingStory, setLoadingStory] = useState(false);
  const [projectBusy, setProjectBusy] = useState(false);
  const [chapterBusy, setChapterBusy] = useState(false);
  const [projectResult, setProjectResult] = useState(null);
  const [chapterResult, setChapterResult] = useState(null);
  const [chapterIndex, setChapterIndex] = useState(0);
  const [editMode, setEditMode] = useState("author_voice");
  const [drafts, setDrafts] = useState([]);
  const [compiledManuscript, setCompiledManuscript] = useState(null);
  const [uploadedManuscript, setUploadedManuscript] = useState(null);
  const [uploadPasteText, setUploadPasteText] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadNotice, setUploadNotice] = useState({ type: "", message: "" });
  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [projectNotice, setProjectNotice] = useState({ type: "", message: "" });
  const [chapterNotice, setChapterNotice] = useState({ type: "", message: "" });

  const mountedRef = useRef(true);
  const initialStoryIdRef = useRef("");
  const requestLockRef = useRef({ project: false, chapter: false });

  useEffect(() => {
    mountedRef.current = true;
    initialStoryIdRef.current = parseStoryIdFromUrl();
    if (!initialStoryIdRef.current && typeof window !== "undefined") {
      initialStoryIdRef.current = safeText(window.localStorage.getItem("ns.selectedStoryId") || window.localStorage.getItem("ns.writer.focusStoryId"));
    }
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectedId = safeText(getId(selectedStory));
  const isUploadedSource = !!uploadedManuscript;
  const canonScenes = useMemo(() => approvedCanonScenes(selectedStory), [selectedStory]);
  const totalWords = useMemo(() => canonScenes.reduce((sum, scene) => sum + countWords(safeSceneText(scene)), 0), [canonScenes]);
  const storyStamp = safeText(selectedStory?.updatedAt || selectedStory?.lastActivityAt || selectedStory?.createdAt || "no-date");
  const soulMapKey = useMemo(() => buildSoulMapStorageKey(selectedId, canonScenes.length, storyStamp), [selectedId, canonScenes.length, storyStamp]);
  const draftKey = useMemo(() => buildDraftStorageKey(selectedId), [selectedId]);

  const ownedStories = useMemo(() => {
    return stories.filter((story) => isStoryOwnedByCurrentUser(story, user));
  }, [stories, user]);

  const filteredStories = useMemo(() => {
    return ownedStories.filter((story) => storyMatchesQuery(story, searchInput));
  }, [ownedStories, searchInput]);

  async function fetchStoriesList() {
    const payload = typeof api.getStoriesList === "function"
      ? await api.getStoriesList({ limit: BOARD_LIST_LIMIT })
      : await api("/api/stories?mode=list");
    return normalizeStoriesListResponse(payload);
  }

  async function openStory(id) {
    const cleanId = safeText(id);
    if (!cleanId) return;

    const listedStory = stories.find((story) => getIdAliases(story).includes(cleanId));
    if (listedStory && !isStoryOwnedByCurrentUser(listedStory, user)) {
      setSelectedStory(null);
      setProjectResult(null);
      setChapterResult(null);
      setCompiledManuscript(null);
      setEditorialBoardStoryIdInUrl("");
      setActionSuccess("");
      setActionError(ui.notOwnerError);
      return;
    }

    try {
      setLoadingStory(true);
      setUploadedManuscript(null);
      setUploadNotice({ type: "", message: "" });
      setActionError("");
      setActionSuccess("");
      const full = typeof api.getStoryById === "function"
        ? await api.getStoryById(cleanId)
        : await api(`/api/stories/${encodeURIComponent(cleanId)}`);
      if (!mountedRef.current) return;

      if (!isStoryOwnedByCurrentUser(full, user)) {
        setSelectedStory(null);
        setProjectResult(null);
        setChapterResult(null);
        setCompiledManuscript(null);
        setEditorialBoardStoryIdInUrl("");
        setActionError(ui.notOwnerError);
        return;
      }

      setSelectedStory(full || null);
      const finalId = safeText(getId(full) || cleanId);
      setEditorialBoardStoryIdInUrl(finalId);
      try {
        window.localStorage.setItem("ns.selectedStoryId", finalId);
        window.localStorage.setItem("ns.writer.focusStoryId", finalId);
      } catch {}
    } catch (err) {
      if (mountedRef.current) setActionError(safeText(err?.message) || "Could not load story.");
    } finally {
      if (mountedRef.current) setLoadingStory(false);
    }
  }

  async function loadStories() {
    if (!isAuthenticated) return;
    try {
      setLoadingList(true);
      setActionError("");
      const arr = await fetchStoriesList();
      if (!mountedRef.current) return;
      setStories(arr);
      const owned = arr.filter((story) => isStoryOwnedByCurrentUser(story, user));
      const preferred = initialStoryIdRef.current;
      if (preferred) {
        const found = owned.find((story) => getIdAliases(story).includes(preferred));
        await openStory(getId(found) || preferred);
      } else if (!selectedStory && owned.length) {
        await openStory(getId(owned[0]));
      } else if (!owned.length) {
        setSelectedStory(null);
        setActionError(ui.noOwnedStories);
      }
    } catch (err) {
      if (mountedRef.current) setActionError(safeText(err?.message) || "Could not load stories.");
    } finally {
      if (mountedRef.current) setLoadingList(false);
    }
  }

  useEffect(() => {
    loadStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    setProjectResult(null);
    setChapterResult(null);
    setCompiledManuscript(null);
    setProjectNotice({ type: "", message: "" });
    setChapterNotice({ type: "", message: "" });
    setChapterIndex(0);
    if (!soulMapKey || typeof window === "undefined") return;
    try {
      const cached = window.localStorage.getItem(soulMapKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.soulMap) setProjectResult(parsed);
      }
    } catch {}
  }, [soulMapKey]);

  useEffect(() => {
    setDrafts([]);
    setCompiledManuscript(null);
    if (!draftKey || typeof window === "undefined") return;
    try {
      const saved = window.localStorage.getItem(draftKey);
      const parsed = saved ? JSON.parse(saved) : [];
      setDrafts(Array.isArray(parsed) ? parsed.slice(0, 12) : []);
    } catch {
      setDrafts([]);
    }
  }, [draftKey]);


  function applyUploadedManuscript(payload, sourceName = "") {
    const normalized = payload?.chapters?.length ? payload : parsePlainTextManuscript(payload?.manuscriptText || "", payload?.title || sourceName, ui);
    const chapters = safeArray(normalized.chapters).filter((chapter) => safeText(chapter?.text));
    if (!chapters.length) throw new Error(ui.uploadFailed);

    const upload = {
      uploadId: safeText(normalized.uploadId) || `uploaded-${Date.now()}`,
      title: safeText(normalized.title) || safeText(sourceName) || ui.uploadedManuscriptTitle,
      fileName: safeText(normalized.fileName || sourceName),
      authorName: safeText(normalized.authorName) || ui.uploadedAuthor,
      chapters,
      manuscriptText: safeText(normalized.manuscriptText) || chapters.map((chapter) => `${chapter.number || ""}. ${chapter.title}\n\n${chapter.text}`).join("\n\n---\n\n"),
      createdAt: new Date().toISOString(),
    };
    const pseudo = uploadedManuscriptToPseudoStory(upload, ui);
    setUploadedManuscript(upload);
    setSelectedStory(pseudo);
    setEditorialBoardStoryIdInUrl("");
    setProjectResult(null);
    setChapterResult(null);
    setCompiledManuscript(null);
    setDrafts([]);
    setChapterIndex(0);
    setProjectNotice({ type: "", message: "" });
    setChapterNotice({ type: "", message: "" });
    setActionError("");
    setActionSuccess("");
    setUploadNotice({ type: "success", message: ui.uploadReady });
  }

  async function handleUploadManuscriptFile(event) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    try {
      setUploadBusy(true);
      setUploadNotice({ type: "working", message: ui.uploadingManuscript });
      const form = new FormData();
      form.append("file", file);
      form.append("language", lang);
      const response = await fetch("/api/ai/manuscript/upload-source", {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) throw new Error(payload?.error || ui.uploadFailed);
      applyUploadedManuscript(payload, file.name);
    } catch (err) {
      try {
        if (/\.txt$|\.md$|\.markdown$/i.test(file.name || "") || /^text\//i.test(file.type || "")) {
          const text = await file.text();
          applyUploadedManuscript(parsePlainTextManuscript(text, file.name.replace(/\.[^.]+$/, ""), ui), file.name);
          return;
        }
      } catch {}
      setUploadNotice({ type: "error", message: safeText(err?.message) || ui.uploadFailed });
    } finally {
      setUploadBusy(false);
      if (event?.target) event.target.value = "";
    }
  }

  function handleUsePastedManuscript() {
    try {
      setUploadNotice({ type: "working", message: ui.uploadingManuscript });
      const parsed = parsePlainTextManuscript(uploadPasteText, ui.uploadedManuscriptTitle, ui);
      applyUploadedManuscript(parsed, ui.uploadedManuscriptTitle);
      setUploadPasteText("");
    } catch (err) {
      setUploadNotice({ type: "error", message: safeText(err?.message) || ui.uploadFailed });
    }
  }

  async function handleCreateProject() {
    if (requestLockRef.current.project || projectBusy) {
      setProjectNotice({ type: "error", message: ui.stillWorking });
      return;
    }
    if (!selectedStory || !selectedId || canonScenes.length === 0) {
      setActionSuccess("");
      setActionError("");
      setProjectNotice({ type: "error", message: ui.noCanon });
      return;
    }
    requestLockRef.current.project = true;
    try {
      setProjectBusy(true);
      setChapterBusy(false);
      setActionError("");
      setActionSuccess("");
      setProjectNotice({ type: "working", message: ui.creatingProjectStatus });
      setChapterNotice({ type: "", message: "" });
      setProjectResult(null);
      setChapterResult(null);
      setCompiledManuscript(null);
      const payload = isUploadedSource
        ? await api.post("/api/ai/manuscript/uploaded-soul-map", {
            title: safeText(selectedStory?.title) || ui.uploadedManuscriptTitle,
            language: lang,
            chapters: uploadedManuscript?.chapters || [],
            manuscriptText: uploadedManuscript?.manuscriptText || "",
          })
        : await api.post("/api/ai/manuscript/soul-map", {
            storyId: selectedId,
            language: lang,
          });
      setProjectResult(payload || null);
      if (payload?.soulMap && soulMapKey && typeof window !== "undefined") {
        try { window.localStorage.setItem(soulMapKey, JSON.stringify(payload)); } catch {}
      }
      setProjectNotice({ type: "success", message: ui.projectSuccess });
    } catch (err) {
      const message = userFacingApiError(err, ui.projectFailed, ui);
      if (isAccessOrNotFoundError(err)) {
        setProjectNotice({ type: "error", message });
      } else {
        const fallback = buildLocalEditorialProject({ story: selectedStory, canonScenes, ui, lang });
        setProjectResult(fallback);
        if (fallback?.soulMap && soulMapKey && typeof window !== "undefined") {
          try { window.localStorage.setItem(soulMapKey, JSON.stringify(fallback)); } catch {}
        }
        setProjectNotice({ type: "warning", message: ui.localProjectFallback });
      }
    } finally {
      requestLockRef.current.project = false;
      setProjectBusy(false);
    }
  }

  async function handleRunChapterPass() {
    if (requestLockRef.current.chapter || chapterBusy) {
      setChapterNotice({ type: "error", message: ui.stillWorking });
      return;
    }
    if (!selectedStory || !selectedId || !projectResult?.soulMap) {
      setActionSuccess("");
      setActionError("");
      setChapterNotice({ type: "error", message: ui.notReady });
      return;
    }
    requestLockRef.current.chapter = true;
    try {
      setChapterBusy(true);
      setActionError("");
      setActionSuccess("");
      setChapterNotice({ type: "working", message: ui.runningPassStatus });
      setChapterResult(null);
      setCompiledManuscript(null);
      const payload = isUploadedSource
        ? await api.post("/api/ai/manuscript/edit-uploaded-chapter", {
            title: safeText(selectedStory?.title) || ui.uploadedManuscriptTitle,
            language: lang,
            chapterIndex: Number(chapterIndex) || 0,
            editMode,
            chapters: uploadedManuscript?.chapters || [],
            manuscriptText: uploadedManuscript?.manuscriptText || "",
            soulMap: projectResult.soulMap || {},
            continuityLedger: projectResult.continuityLedger || {},
            chapterMap: projectResult.chapterMap || [],
          })
        : await api.post("/api/ai/manuscript/edit-chapter", {
            storyId: selectedId,
            language: lang,
            chapterIndex: Number(chapterIndex) || 0,
            editMode,
            soulMap: projectResult.soulMap || {},
            continuityLedger: projectResult.continuityLedger || {},
            chapterMap: projectResult.chapterMap || [],
          });
      setChapterResult(payload || null);
      setChapterNotice({ type: "success", message: ui.passSuccess });
    } catch (err) {
      setChapterNotice({ type: "error", message: userFacingApiError(err, ui.passFailed, ui) });
    } finally {
      requestLockRef.current.chapter = false;
      setChapterBusy(false);
    }
  }

  async function handleCopyRevision(kind, sourceDraft = null) {
    const source = sourceDraft || chapterResult || {};
    try {
      const isMarked = kind === "marked";
      const raw = isMarked ? source.markedRevision : source.cleanRevision;
      const plainText = isMarked ? manuscriptMarkedRevisionToPlainText(raw) : stripManuscriptEditMarkers(raw);
      const htmlText = isMarked ? manuscriptMarkedRevisionToHtml(raw) : null;
      await copyTextToClipboard({ plainText, htmlText });
      setActionError("");
      setActionSuccess("");
      setChapterNotice({ type: "success", message: isMarked ? ui.copyMarkedSuccess : ui.copyCleanSuccess });
    } catch {
      setActionSuccess("");
      setActionError("");
      setChapterNotice({ type: "error", message: ui.copyFailed });
    }
  }

  function handleSaveDraft() {
    if (!chapterResult || !draftKey || typeof window === "undefined") return;
    const selectedChapter = safeArray(projectResult?.chapterMap)[Number(chapterIndex) || 0] || {};
    const draft = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      createdAt: new Date().toISOString(),
      storyId: selectedId,
      storyTitle: safeText(selectedStory?.title),
      chapterIndex: Number(chapterIndex) || 0,
      chapterNumber: chapterResult.chapterNumber || Number(chapterIndex) + 1,
      chapterTitle: safeText(chapterResult.chapterTitle || selectedChapter.title || `${ui.untitledChapter} ${Number(chapterIndex) + 1}`),
      editMode: chapterResult.editMode || editMode,
      editModeLabel: chapterResult.editModeLabel || editMode,
      cleanRevision: String(chapterResult.cleanRevision || ""),
      markedRevision: String(chapterResult.markedRevision || ""),
      editorialNotes: safeArray(chapterResult.editorialNotes).slice(0, 12),
      continuityWarnings: safeArray(chapterResult.continuityWarnings).slice(0, 12),
      changeSummary: safeArray(chapterResult.changeSummary).slice(0, 16),
      advisoryOnly: true,
      mutatesStory: false,
    };
    const nextDrafts = [draft, ...drafts].slice(0, 12);
    setDrafts(nextDrafts);
    setCompiledManuscript(null);
    try { window.localStorage.setItem(draftKey, JSON.stringify(nextDrafts)); } catch {}
    setActionError("");
    setActionSuccess("");
    setChapterNotice({ type: "success", message: ui.draftSaved });
  }

  function handleCompileEditedManuscript() {
    if (!selectedStory || !canonScenes.length) return;
    const compiled = buildCompiledEditedManuscript({ story: selectedStory, canonScenes, drafts, ui, lang });
    setCompiledManuscript(compiled);
    setActionError("");
    setActionSuccess("");
    setChapterNotice({ type: "success", message: ui.compiledReady });
  }

  async function handleCopyCompiledManuscript() {
    try {
      const compiled = compiledManuscript || buildCompiledEditedManuscript({ story: selectedStory, canonScenes, drafts, ui, lang });
      await copyTextToClipboard({ plainText: compiled.text });
      setCompiledManuscript(compiled);
      setActionError("");
      setActionSuccess("");
      setChapterNotice({ type: "success", message: ui.copyCompiledSuccess });
    } catch {
      setActionSuccess("");
      setActionError("");
      setChapterNotice({ type: "error", message: ui.copyFailed });
    }
  }

  function handleExportCompiledManuscript(kind) {
    try {
      const compiled = compiledManuscript || buildCompiledEditedManuscript({ story: selectedStory, canonScenes, drafts, ui, lang });
      const title = safeText(selectedStory?.title) || ui.untitled;
      const base = `${slugifyFileName(title)}-edited-manuscript`;
      setCompiledManuscript(compiled);
      if (kind === "docx") {
        downloadBlob(createDocxBlobFromText(title, compiled.text), `${base}.docx`);
        setChapterNotice({ type: "success", message: ui.exportDocxSuccess });
        return;
      }
      downloadBlob(createPdfBlobFromText(title, compiled.text), `${base}.pdf`);
      setChapterNotice({ type: "success", message: ui.exportPdfSuccess });
    } catch {
      setChapterNotice({ type: "error", message: ui.exportFailed });
    }
  }

  const chaptersForSelect = safeArray(projectResult?.chapterMap).length ? safeArray(projectResult.chapterMap) : canonScenes.map((scene, idx) => ({ title: safeSceneTitle(scene, ui), summary: clampWords(safeSceneText(scene), 16), index: idx }));

  function renderTaskNotice(notice, busy, busyMessage) {
    const message = busy ? busyMessage : safeText(notice?.message);
    if (!message) return null;
    const kind = busy ? "working" : safeText(notice?.type || "info");
    return (
      <div className={`ns-editorial-board-inline-status is-${kind}`} role="status" aria-live="polite">
        {busy ? <span className="ns-editorial-board-spinner" aria-hidden="true"></span> : null}
        <span>{message}</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="ns-page ns-editorial-board-page">
        <header className="ns-editorial-board-hero">
          <p className="ns-dashboard-brand">NEXTSCENES</p>
          <h1>{ui.title}</h1>
          <p>{ui.loginRequired}</p>
        </header>
      </div>
    );
  }

  return (
    <div className="ns-page ns-editorial-board-page">
      <ProjectBreadcrumbTrail
        onNavigatePage={onNavigatePage}
        lang={lang}
        pageKey="editorialBoard"
        projectTitle={
          selectedStory?.title ||
          projectHubContext?.projectTitle
        }
      />

      <header className="ns-editorial-board-hero">
        <p className="ns-dashboard-brand">NEXTSCENES</p>
        <p className="ns-dashboard-kicker">{ui.kicker}</p>
        <h1>{ui.title}</h1>
        <p>{ui.subtitle}</p>
      </header>

      {(actionError || actionSuccess) ? (
        <div className="ns-dashboard-status-stack ns-editorial-board-status">
          {actionError ? <p className="ns-card-meta ns-dashboard-error">{actionError}</p> : null}
          {actionSuccess ? <p className="ns-card-meta ns-dashboard-success">{actionSuccess}</p> : null}
        </div>
      ) : null}

      <section className="ns-card ns-editorial-board-source-card">
        <div className="ns-editorial-board-source-head">
          <div>
            <h2 className="ns-section-title">{ui.sourceTitle}</h2>
            <p className="ns-card-meta">{ui.existingStory} · {ui.ownerOnlyNote}</p>
          </div>
          <button className="ns-button ns-button-outline" type="button" onClick={loadStories} disabled={loadingList || projectBusy || chapterBusy}>{loadingList ? "…" : ui.refreshStories}</button>
        </div>

        <div className="ns-editorial-board-source-grid">
          <label>
            <span>{ui.chooseStory}</span>
            <select value={isUploadedSource ? "" : selectedId} onChange={(event) => openStory(event.target.value)} disabled={loadingStory || loadingList || projectBusy || chapterBusy || !ownedStories.length}>
              <option value="">{isUploadedSource ? ui.uploadedSource : (ownedStories.length ? ui.noStory : ui.noOwnedStories)}</option>
              {filteredStories.map((story) => {
                const id = safeText(getId(story));
                return <option key={id || safeText(story?.title)} value={id}>{safeText(story?.title) || ui.untitled}</option>;
              })}
            </select>
          </label>
          <label>
            <span>{ui.search}</span>
            <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={ui.search} />
          </label>
        </div>

        <div className="ns-editorial-board-upload-future ns-editorial-board-upload-panel" role="note">
          <strong>{ui.futureUpload}</strong>
          <span>{ui.futureUploadBody}</span>
          <div className="ns-editorial-board-upload-controls">
            <label className="ns-editorial-board-file-upload">
              <span>{uploadBusy ? ui.uploadingManuscript : ui.uploadFile}</span>
              <input type="file" accept=".docx,.txt,.md,.markdown,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={handleUploadManuscriptFile} disabled={uploadBusy || projectBusy || chapterBusy} />
            </label>
            <label className="ns-editorial-board-paste-upload">
              <span>{ui.uploadPasteLabel}</span>
              <textarea value={uploadPasteText} onChange={(event) => setUploadPasteText(event.target.value)} placeholder={ui.uploadPastePlaceholder} disabled={uploadBusy || projectBusy || chapterBusy} rows={4} />
            </label>
            <button className="ns-button ns-button-outline" type="button" onClick={handleUsePastedManuscript} disabled={uploadBusy || projectBusy || chapterBusy || !safeText(uploadPasteText)}>{ui.usePastedManuscript}</button>
          </div>
          {renderTaskNotice(uploadNotice, uploadBusy, ui.uploadingManuscript)}
          {isUploadedSource ? <span className="ns-editorial-board-upload-active">{ui.uploadedSourceNote}</span> : null}
        </div>
      </section>

      {selectedStory ? (
        <section className="ns-card ns-editorial-board-story-card">
          <div className="ns-editorial-board-story-head">
            <div>
              <h2>{safeText(selectedStory?.title) || ui.untitled}</h2>
              <p className="ns-card-meta">
                {ui.author}: {storyAuthorName(selectedStory, ui.unknown)} · {ui.forum}: {safeText(selectedStory?.forum) || ui.unknown}
              </p>
            </div>
            {!isUploadedSource ? (
              <div className="ns-editorial-board-story-actions">
                <button className="ns-button ns-button-outline" type="button" onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("storyDashboard", { storyId: selectedId }) : null)}>{ui.openDashboard}</button>
                <button className="ns-button ns-button-outline" type="button" onClick={() => (typeof onNavigatePage === "function" ? onNavigatePage("writerStudio", { storyId: selectedId }) : null)}>{ui.openWriterStudio}</button>
              </div>
            ) : <span className="ns-editorial-board-upload-active">{ui.uploadedSource}</span>}
          </div>
          <div className="ns-editorial-board-metrics">
            <span><strong>{canonScenes.length}</strong> {isUploadedSource ? ui.sourceChapters : ui.canonChapters}</span>
            <span><strong>{totalWords.toLocaleString()}</strong> {ui.words}</span>
            <span>{ui.updated}: {formatDateTime(selectedStory?.updatedAt || selectedStory?.createdAt, lang) || ui.unknown}</span>
          </div>
          {canonScenes.length === 0 ? <p className="ns-card-meta ns-dashboard-error">{ui.noCanon}</p> : null}
        </section>
      ) : null}

      <main className="ns-editorial-board-workspace">
        <section className="ns-card ns-editorial-board-project-card">
          <div className="ns-editorial-board-work-head">
            <div>
              <h2>{ui.soulMap}</h2>
              <p className="ns-card-meta">{ui.authorDecides}</p>
            </div>
            <button className="ns-button ns-button-primary ns-editorial-board-action-button" type="button" onClick={handleCreateProject} disabled={projectBusy || chapterBusy || !selectedId || canonScenes.length === 0}>
              {projectBusy ? <><span className="ns-editorial-board-button-spinner" aria-hidden="true"></span>{ui.creatingProject}</> : projectResult?.soulMap ? ui.refreshProject : ui.createProject}
            </button>
          </div>

          {renderTaskNotice(projectNotice, projectBusy, ui.creatingProjectStatus)}

          {projectResult?.soulMap ? (
            <div className="ns-manuscript-editor-result ns-editorial-board-result">
              <div className="ns-manuscript-editor-result-head">
                <strong>{ui.editorialSummary}</strong>
                <span>{ui.advisoryOnly}</span>
              </div>
              <p>{projectResult.soulMap?.editorialSummary || projectResult.soulMap?.centralTheme || ""}</p>

              <div className="ns-manuscript-editor-compact-stack">
                <details className="ns-manuscript-editor-details ns-manuscript-compact-detail">
                  <summary><strong>{ui.voice}</strong><span>{safeArray(projectResult.soulMap?.voiceRules).length}</span></summary>
                  <ul>{safeArray(projectResult.soulMap?.voiceRules).map((item, idx) => <li key={`voice-${idx}`}>{item}</li>)}</ul>
                </details>
                <details className="ns-manuscript-editor-details ns-manuscript-compact-detail">
                  <summary><strong>{ui.protect}</strong><span>{safeArray(projectResult.soulMap?.protectedElements).length}</span></summary>
                  <ul>{safeArray(projectResult.soulMap?.protectedElements).map((item, idx) => <li key={`protect-${idx}`}>{item}</li>)}</ul>
                </details>
                <details className="ns-manuscript-editor-details ns-manuscript-compact-detail">
                  <summary><strong>{ui.continuity}</strong><span>{safeArray(projectResult.continuityLedger?.unresolvedPromises).length}</span></summary>
                  <ul>{safeArray(projectResult.continuityLedger?.unresolvedPromises).map((item, idx) => <li key={`continuity-${idx}`}>{item}</li>)}</ul>
                </details>
                <details className="ns-manuscript-editor-details ns-manuscript-compact-detail">
                  <summary><strong>{ui.nextSteps}</strong><span>{safeArray(projectResult.nextSteps).length}</span></summary>
                  <ul>{safeArray(projectResult.nextSteps).map((item, idx) => <li key={`step-${idx}`}>{item}</li>)}</ul>
                </details>
              </div>

              <details className="ns-manuscript-editor-details ns-manuscript-chapter-map-details">
                <summary><strong>{ui.chapterMap}</strong><span>{chaptersForSelect.length} {isUploadedSource ? ui.sourceChapters : ui.canonChapters}</span></summary>
                <ol>{chaptersForSelect.map((chapter, idx) => <li key={`map-${idx}`}><strong>{chapter?.title || `${idx + 1}. ${ui.untitledChapter}`}</strong><span>{chapter?.function || chapter?.summary || ""}</span></li>)}</ol>
              </details>
            </div>
          ) : (
            <div className="ns-editorial-board-empty-state">
              <strong>{ui.createProject}</strong>
              <span>{ui.authorDecides}</span>
            </div>
          )}
        </section>

        <section className="ns-card ns-editorial-board-chapter-card">
          <div className="ns-editorial-board-work-head">
            <div>
              <h2>{ui.chapterPass}</h2>
              <p className="ns-card-meta">{ui.decisionBody}</p>
            </div>
          </div>

          <div className="ns-manuscript-chapter-editor-controls ns-editorial-board-pass-controls">
            <label>
              <span>{ui.chooseChapter}</span>
              <select value={chapterIndex} onChange={(event) => setChapterIndex(Number(event.target.value) || 0)} disabled={chapterBusy || projectBusy || !chaptersForSelect.length}>
                {chaptersForSelect.map((chapter, idx) => <option key={`chapter-${idx}`} value={idx}>{idx + 1}. {chapter?.title || ui.untitledChapter}</option>)}
              </select>
            </label>
            <label>
              <span>{ui.editMode}</span>
              <select value={editMode} onChange={(event) => setEditMode(event.target.value)} disabled={chapterBusy || projectBusy}>
                <option value="light_polish">{ui.lightPolish}</option>
                <option value="author_voice">{ui.authorVoice}</option>
                <option value="continuity">{ui.continuityPass}</option>
                <option value="deep_editorial">{ui.deepEditorial}</option>
              </select>
            </label>
            <button className="ns-button ns-editorial-board-run-pass-button" type="button" onClick={handleRunChapterPass} disabled={chapterBusy || projectBusy || !projectResult?.soulMap}>
              {chapterBusy ? <><span className="ns-editorial-board-button-spinner" aria-hidden="true"></span>{ui.runningPass}</> : ui.runPass}
            </button>
          </div>

          {renderTaskNotice(chapterNotice, chapterBusy, ui.runningPassStatus)}

          {chapterResult ? (
            <div className="ns-manuscript-chapter-editor-result ns-editorial-board-result">
              <div className="ns-manuscript-editor-result-head">
                <strong>{ui.result}</strong>
                <span>{ui.advisoryOnly}</span>
              </div>

              <div className="ns-manuscript-author-decision-panel" role="note">
                <div>
                  <strong>{ui.decisionControls}</strong>
                  <span>{ui.decisionBody}</span>
                </div>
                <div className="ns-manuscript-author-decision-actions">
                  <button className="ns-button ns-button-outline" type="button" onClick={() => handleCopyRevision("clean")}>{ui.copyClean}</button>
                  <button className="ns-button ns-button-outline" type="button" onClick={() => handleCopyRevision("marked")}>{ui.copyMarked}</button>
                  <button className="ns-button ns-button-primary ns-manuscript-editor-main-button" type="button" onClick={handleSaveDraft}>{ui.saveDraft}</button>
                  <span className="ns-manuscript-canon-postponed">{ui.applyPostponed}</span>
                </div>
              </div>

              <div className="ns-manuscript-editor-compact-stack">
                <details className="ns-manuscript-editor-details ns-manuscript-compact-detail">
                  <summary><strong>{ui.notes}</strong><span>{safeArray(chapterResult.editorialNotes).length}</span></summary>
                  <ul>{safeArray(chapterResult.editorialNotes).map((item, idx) => <li key={`note-${idx}`}>{item}</li>)}</ul>
                </details>
                <details className="ns-manuscript-editor-details ns-manuscript-compact-detail">
                  <summary><strong>{ui.warnings}</strong><span>{safeArray(chapterResult.continuityWarnings).length}</span></summary>
                  <ul>{safeArray(chapterResult.continuityWarnings).map((item, idx) => <li key={`warning-${idx}`}>{item}</li>)}</ul>
                </details>
                <details className="ns-manuscript-editor-details ns-manuscript-compact-detail">
                  <summary><strong>{ui.changes}</strong><span>{safeArray(chapterResult.changeSummary).length}</span></summary>
                  <ul>{safeArray(chapterResult.changeSummary).map((item, idx) => <li key={`change-${idx}`}>{item}</li>)}</ul>
                </details>
              </div>

              <details className="ns-manuscript-editor-details">
                <summary><strong>{ui.marked}</strong></summary>
                <pre className="ns-manuscript-editor-text-output">{renderMarkedText(chapterResult.markedRevision || "")}</pre>
              </details>
              <details className="ns-manuscript-editor-details">
                <summary><strong>{ui.clean}</strong></summary>
                <pre className="ns-manuscript-editor-text-output">{chapterResult.cleanRevision || ""}</pre>
              </details>
              <em className="ns-manuscript-editor-advisory">{chapterResult.authorDecisionReminder || ui.authorDecides}</em>
            </div>
          ) : null}

          {drafts.length ? (
            <details className="ns-manuscript-editor-details ns-manuscript-saved-drafts ns-editorial-board-drafts">
              <summary><strong>{ui.savedDrafts}</strong><span>{drafts.length}</span></summary>
              <p className="ns-card-meta">{ui.savedDraftsHelp}</p>
              <div className="ns-manuscript-saved-draft-list">
                {drafts.slice(0, 8).map((draft) => (
                  <div className="ns-manuscript-saved-draft-item" key={draft.id || `${draft.chapterNumber}-${draft.createdAt}`}>
                    <div>
                      <strong>{draft.chapterNumber}. {draft.chapterTitle}</strong>
                      <span>{formatDateTime(draft.createdAt, lang)} · {draft.editModeLabel || draft.editMode || ui.localOnly}</span>
                    </div>
                    <div className="ns-manuscript-saved-draft-actions">
                      <button className="ns-button ns-button-outline" type="button" onClick={() => handleCopyRevision("clean", draft)}>{ui.copyCleanSmall}</button>
                      <button className="ns-button ns-button-outline" type="button" onClick={() => handleCopyRevision("marked", draft)}>{ui.copyMarkedSmall}</button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          ) : null}

          {drafts.length ? (
            <section className="ns-editorial-board-compile-panel" aria-label={ui.compiledTitle}>
              <div>
                <strong>{ui.compiledTitle}</strong>
                <span>{ui.recompileHelp}</span>
              </div>
              <div className="ns-editorial-board-compile-actions">
                <button className="ns-button ns-editorial-board-gold-button" type="button" onClick={handleCompileEditedManuscript}>{ui.recompile}</button>
                <button className="ns-button ns-button-outline" type="button" onClick={handleCopyCompiledManuscript}>{ui.copyCompiled}</button>
                <button className="ns-button ns-button-outline" type="button" onClick={() => handleExportCompiledManuscript("docx")}>{ui.exportDocx}</button>
                <button className="ns-button ns-button-outline" type="button" onClick={() => handleExportCompiledManuscript("pdf")}>{ui.exportPdf}</button>
              </div>
              {compiledManuscript ? (
                <div className="ns-editorial-board-compiled-output">
                  <p>
                    <strong>{compiledManuscript.usedDrafts}</strong> {ui.usedDrafts} · <strong>{compiledManuscript.canonFallbacks}</strong> {ui.canonFallbacks}
                  </p>
                  <pre className="ns-manuscript-editor-text-output">{compiledManuscript.text}</pre>
                </div>
              ) : null}
            </section>
          ) : null}
        </section>
      </main>
    </div>
  );
}
